import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { MapPin, CheckCircle2, ArrowRight, ChevronDown, Navigation, List, Map as MapIcon } from 'lucide-react';
import { api } from '@/lib/api';
import { useBranding } from '@/lib/branding';
import { useVenue } from '@/lib/venue';
import { LocationPicker } from '@/components/LocationPicker';
import { StarRating } from '@/components/ui/StarRating';
import { VenueMap } from '@/components/VenueMap';

// The "pick a venue, then browse its courts" discovery step. Reached the
// first time a customer starts a booking/membership purchase, or any time
// afterwards via the header's "change venue" link — see lib/venue.jsx for
// how the choice then persists across navigation.
//
// Location (address-picker change): a Swiggy/Zomato-style location bar
// sits at the top — tapping it opens the same LocationPicker used for
// venues, so a customer can search an address or drop a pin instead of
// only being able to say "use my current GPS position". On first load we
// still make one silent attempt at the browser's own geolocation (the
// fastest path when it's already granted); if that's denied or skipped,
// the bar just reads "Select your location" until the customer taps it.
// Nothing here is persisted — reflects the same "ask fresh every time"
// philosophy as venue selection itself (see lib/venue.jsx).
export default function Venues() {
  const { venues, loading, selectedVenueId } = useVenue();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = location.state?.redirectTo || '/app/book';

  // 'idle' | 'locating' | 'sorted' | 'unavailable'
  const [geoStatus, setGeoStatus] = useState('idle');
  const [nearby, setNearby] = useState(null);
  const [currentAddress, setCurrentAddress] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);
  const [view, setView] = useState('list'); // 'list' | 'map'
  const [coords, setCoords] = useState(null); // { lat, lng } once known, for re-filtering by sport

  // Multi-sport: which sports exist platform-wide (populates the filter
  // dropdown) and which one, if any, is picked — refiltered into whichever
  // list source (nearby vs plain) is currently active. An academy "offers"
  // a sport if it has at least one active court for it (see
  // app.routers.academies._academy_ids_for_sport).
  const [sports, setSports] = useState([]);
  const [sportId, setSportId] = useState('');
  const [sportVenues, setSportVenues] = useState(null); // plain-list sport filter result, when no location is known yet

  useEffect(() => {
    api.get('/sports').then((r) => setSports(r.data)).catch(() => {});
  }, []);

  // Discovery radius — the super_admin's nearby_radius_km setting, applied
  // server-side. Academies outside it (but with coordinates set) are
  // excluded rather than just sorted last; ungeotagged academies still come
  // back appended (see academies.py) regardless of this radius.
  const { nearby_radius_km: radiusKm } = useBranding();

  const fetchNearby = async (lat, lng, sport) => {
    setCoords({ lat, lng });
    try {
      const { data } = await api.get('/academies/nearby', { params: { lat, lng, sport_id: sport || undefined } });
      setNearby(data);
      setGeoStatus('sorted');
    } catch {
      setGeoStatus('unavailable');
    }
  };

  const requestLocation = () => {
    if (!navigator.geolocation) {
      setGeoStatus('unavailable');
      return;
    }
    setGeoStatus('locating');
    navigator.geolocation.getCurrentPosition(
      (pos) => fetchNearby(pos.coords.latitude, pos.coords.longitude, sportId),
      () => setGeoStatus('unavailable'),
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 5 * 60 * 1000 },
    );
  };

  useEffect(() => { requestLocation(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onLocationConfirmed = ({ address, latitude, longitude }) => {
    setCurrentAddress(address);
    setPickerOpen(false);
    setGeoStatus('locating');
    fetchNearby(latitude, longitude, sportId);
  };

  const onSportChange = async (id) => {
    setSportId(id);
    if (coords) {
      // Already have a location — refilter the distance-sorted list.
      fetchNearby(coords.lat, coords.lng, id);
      return;
    }
    // No location yet — filter the plain (unsorted) directory instead.
    if (!id) { setSportVenues(null); return; }
    try {
      const { data } = await api.get('/academies', { params: { sport_id: id } });
      setSportVenues(data);
    } catch { /* keep the previous filtered list on failure */ }
  };

  // Picking a venue opens its details page (photos, facilities, amenities,
  // offers); the venue is selected from there via "Book a court"/"See plans".
  const choose = (id) => {
    navigate(`/app/venues/${id}`, { state: { redirectTo } });
  };

  const list = nearby || sportVenues || venues;

  return (
    <div className="space-y-6" data-testid="venues-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-volt mb-2">Choose Your Venue</div>
          <h1 className="font-heading font-black text-4xl sm:text-5xl uppercase tracking-tight">Where are you playing?</h1>
        </div>
        <div className="flex border border-white/10 shrink-0" data-testid="venues-view-toggle">
          <button
            type="button"
            onClick={() => setView('list')}
            className={`px-3 py-2 flex items-center gap-1.5 text-[11px] uppercase tracking-[0.15em] ${view === 'list' ? 'bg-volt text-black' : 'text-white/50 hover:text-white'}`}
          >
            <List size={13} /> List
          </button>
          <button
            type="button"
            onClick={() => setView('map')}
            data-testid="venues-view-map"
            className={`px-3 py-2 flex items-center gap-1.5 text-[11px] uppercase tracking-[0.15em] ${view === 'map' ? 'bg-volt text-black' : 'text-white/50 hover:text-white'}`}
          >
            <MapIcon size={13} /> Map
          </button>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <button
          type="button"
          onClick={() => setPickerOpen(true)}
          className="flex-1 flex items-center gap-3 bg-surface border border-white/10 hover:border-volt/50 transition-colors px-5 py-3.5 text-left"
          data-testid="venues-location-bar"
        >
          <MapPin size={16} className="text-volt shrink-0" />
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.2em] text-white/40">
              {geoStatus === 'locating' ? 'Locating…' : geoStatus === 'sorted' ? 'Sorted by distance from' : 'Your location'}
            </div>
            <div className="text-sm text-white truncate max-w-xs">
              {currentAddress || (geoStatus === 'locating' ? 'Detecting…' : 'Select your location')}
            </div>
          </div>
          <ChevronDown size={14} className="text-white/30 ml-1 shrink-0" />
        </button>

        {sports.length > 1 && (
          <select
            value={sportId}
            onChange={(e) => onSportChange(e.target.value)}
            className="bg-surface border border-white/10 hover:border-volt/50 transition-colors px-5 py-3.5 text-sm text-white outline-none sm:w-56"
            data-testid="venues-sport-filter"
          >
            <option value="">All sports</option>
            {sports.map((s) => (<option key={s.id} value={s.id}>{s.name}</option>))}
          </select>
        )}
      </div>

      {loading ? (
        <div className="text-white/40 text-sm">Loading venues…</div>
      ) : view === 'map' ? (
        <div className="h-[60vh] border border-white/10" data-testid="venues-map-container">
          <VenueMap
            venues={list}
            selectedVenueId={selectedVenueId}
            onSelect={choose}
            center={nearby?.[0]?.latitude != null ? { lat: nearby[0].latitude, lng: nearby[0].longitude } : undefined}
          />
        </div>
      ) : list.length === 0 ? (
        <div className="flex flex-col items-center gap-2 py-16 text-center">
          <MapPin size={22} className="text-white/30" />
          <div className="text-white font-medium">
            {nearby ? `No venues within ${radiusKm} km` : sportId ? 'No venues offer that sport yet' : 'No venues yet'}
          </div>
          <div className="text-sm text-white/50">Try a different {nearby && sportId ? 'location or sport' : nearby ? 'location' : 'sport'}, or see every venue instead.</div>
          <button
            type="button"
            onClick={() => { setNearby(null); setSportVenues(null); setSportId(''); }}
            data-testid="venues-show-all"
            className="text-volt text-sm font-semibold mt-2 hover:underline"
          >
            Show all venues
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {list.map((v) => {
            const active = v.id === selectedVenueId;
            return (
              <button
                type="button"
                key={v.id}
                onClick={() => choose(v.id)}
                data-testid={`venue-pick-${v.id}`}
                className={`text-left bg-surface border ${active ? 'border-volt volt-glow' : 'border-white/10 hover:border-white/30'} transition-colors p-6 flex flex-col gap-3`}
              >
                <div className="flex items-start justify-between">
                  <div className="w-10 h-10 rounded-full bg-volt/10 flex items-center justify-center">
                    <MapPin size={18} className="text-volt" />
                  </div>
                  {active && <CheckCircle2 size={18} className="text-volt" />}
                </div>
                <div>
                  <div className="font-heading font-bold text-xl uppercase">{v.name}</div>
                  {(v.address || v.city) && (
                    <div className="text-sm text-white/50 mt-1">{[v.address, v.city].filter(Boolean).join(', ')}</div>
                  )}
                  {v.review_count > 0 && (
                    <div className="flex items-center gap-1.5 mt-1.5">
                      <StarRating value={v.average_rating} size={12} />
                      <span className="text-xs text-white/40">{v.average_rating} ({v.review_count})</span>
                    </div>
                  )}
                  {v.distance_km != null && (
                    <div className="text-xs text-volt mt-1.5 flex items-center gap-1">
                      <Navigation size={11} /> {v.distance_km} km away
                    </div>
                  )}
                </div>
                <div className="mt-auto flex items-center gap-1 text-[11px] uppercase tracking-[0.2em] text-volt">
                  {active ? 'Currently selected · view details' : 'View venue'} <ArrowRight size={12} />
                </div>
              </button>
            );
          })}
        </div>
      )}

      <LocationPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onConfirm={onLocationConfirmed}
        title="Select your location"
      />
    </div>
  );
}
